import React, { useState, useEffect } from 'react';
import { 
  Headphones,
  Clock, 
  User, 
  AlertCircle,
  CheckCircle,
  MessageSquare,
  Video,
  Filter,
  Search,
  ChevronRight,
  Phone,
  Mail,
  Calendar,
  TrendingUp,
  Timer,
  Star,
  MoreVertical,
  RefreshCw,
  Send,
  UserPlus
} from 'lucide-react';
import { BrandingProvider } from '../../contexts/BrandingContext';
import { 
  BrandedHeader, 
  BrandedButton, 
  BrandedCard, 
  BrandedBadge,
  BrandedInput
} from '../../components/branding/BrandedComponents';
import { useAuth } from '../../contexts/AuthContext';

interface SupportTicket {
  ticketId: string;
  customerName: string;
  email: string;
  category: 'technical' | 'billing' | 'product' | 'general';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  subject: string;
  description: string;
  status: 'new' | 'in_progress' | 'waiting' | 'resolved' | 'closed';
  createdAt: string;
  waitTime: number; // in minutes
  assignedTo?: string;
  previousInteractions?: number;
  satisfaction?: number;
  attachments?: string[];
}

interface AgentDashboardProps {
  agentName?: string;
  companyName?: string;
  onAcceptTicket?: (ticketId: string) => void;
  onStartVideoCall?: (ticketId: string) => void;
}

export const AgentDashboard: React.FC<AgentDashboardProps> = ({
  agentName = 'Support Agent',
  companyName = 'Support Center',
  onAcceptTicket,
  onStartVideoCall
}) => {
  return (
    <BrandingProvider templateType="business" initialBranding={{ companyName }}>
      <AgentDashboardContent 
        agentName={agentName}
        companyName={companyName}
        onAcceptTicket={onAcceptTicket}
        onStartVideoCall={onStartVideoCall}
      />
    </BrandingProvider>
  );
};

const AgentDashboardContent: React.FC<AgentDashboardProps> = ({
  agentName = 'Support Agent',
  companyName = 'Support Center',
  onAcceptTicket,
  onStartVideoCall
}) => {
  const { getCurrentOrganizationId, isAuthenticated } = useAuth();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAccepting, setIsAccepting] = useState<string | null>(null); // ticketId being accepted
  const [filter, setFilter] = useState<'all' | 'new' | 'my_tickets'>('new');
  const [searchQuery, setSearchQuery] = useState('');
  const [agentStatus, setAgentStatus] = useState<'available' | 'busy' | 'offline'>('available');
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [apiKeyLoading, setApiKeyLoading] = useState(true);
  const [showApiKeyInput, setShowApiKeyInput] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  
  // Get current organization from authentication context
  const orgId = getCurrentOrganizationId();
  
  // Load API key on component mount (exactly like legal template)
  useEffect(() => {
    const loadApiKey = () => {
      if (!isAuthenticated || !orgId) {
        console.log('⚠️ AGENT: Not authenticated or no org ID');
        setApiKeyLoading(false);
        return;
      }
      
      console.log('🔐 AGENT: Loading API key for organization:', orgId);
      
      // Check localStorage for stored API key
      const storedKey = localStorage.getItem(`agent_api_key_${orgId}`);
      if (storedKey) {
        console.log('✅ AGENT: Found stored API key for organization');
        setApiKey(storedKey);
      } else {
        console.warn('⚠️ AGENT: No API key found for organization');
        setShowApiKeyInput(true);
      }
      
      setApiKeyLoading(false);
    };
    
    loadApiKey();
  }, [isAuthenticated, orgId]);
  
  // Handle API key submission
  const handleApiKeySubmit = () => {
    if (apiKeyInput && apiKeyInput.startsWith('pk_live_')) {
      localStorage.setItem(`agent_api_key_${orgId}`, apiKeyInput);
      setApiKey(apiKeyInput);
      setShowApiKeyInput(false);
      setApiKeyInput('');
      setError(null);
      console.log('✅ AGENT: API key saved for organization');
    } else {
      setError('Invalid API key format. Must start with pk_live_');
    }
  };
  
  // Clear API key (for testing/switching)
  const clearApiKey = () => {
    localStorage.removeItem(`agent_api_key_${orgId}`);
    setApiKey(null);
    setShowApiKeyInput(true);
    setTickets([]);
    console.log('🗑️ AGENT: API key cleared');
  };
  
  // Log current state for debugging  
  console.log('🔐 AGENT: Current organization ID:', orgId);
  console.log('🔑 AGENT: Using API key (first 20 chars):', apiKey ? apiKey.substring(0, 20) + '...' : 'Not set');

  // Fetch tickets on component mount and set up polling (exactly like legal template)
  useEffect(() => {
    if (!apiKey || apiKeyLoading) {
      return;
    }
    
    console.log('🚀 AGENT: Support Agent Dashboard mounted, starting ticket monitoring...');
    fetchTickets();
    
    // Poll for new tickets every 30 seconds
    console.log('⏰ AGENT: Setting up polling interval (30 seconds)...');
    const interval = setInterval(() => {
      console.log('🔄 AGENT: Polling interval triggered - checking for new tickets...');
      fetchTickets();
    }, 30000);
    
    return () => {
      console.log('🛑 AGENT: Cleaning up polling interval...');
      clearInterval(interval);
    };
  }, [apiKey, apiKeyLoading]);

  const fetchTickets = async () => {
    console.log('🔍 AGENT: Fetching pending tickets...');
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch('https://dev-platform-api-dev.benjiemalinao879557.workers.dev/api/v1/tickets/pending', {
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('📡 AGENT: API Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔴 AGENT: API Error response:', errorText);
        
        if (response.status === 401) {
          setError('Invalid API key. Please check your key and try again.');
          clearApiKey();
        } else {
          throw new Error(`Failed to fetch tickets: ${response.status}`);
        }
        return;
      }
      
      const data = await response.json();
      console.log('📊 AGENT: Received ticket data:', data);
      
      const newTickets = data.tickets || [];
      
      // Update tickets and log changes (exactly like legal template)
      setTickets(prevTickets => {
        if (prevTickets.length !== newTickets.length) {
          if (newTickets.length > prevTickets.length) {
            const newCount = newTickets.length - prevTickets.length;
            console.log('🔔 AGENT: NEW SUPPORT TICKETS DETECTED!', {
              previousCount: prevTickets.length,
              newCount: newTickets.length,
              addedTickets: newCount
            });
          }
        }
        console.log('✅ AGENT: Successfully updated ticket list. Total pending:', newTickets.length);
        return newTickets;
      });
      
    } catch (err) {
      console.error('🔴 AGENT: Error fetching tickets:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch tickets');
    } finally {
      setIsLoading(false);
    }
  };

  // Accept ticket (exactly like legal template)
  const acceptTicket = async (ticketId: string) => {
    if (!apiKey) {
      setError('No API key available');
      return;
    }
    
    console.log('✅ AGENT: Accepting ticket:', ticketId);
    setIsAccepting(ticketId);
    
    try {
      const response = await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev/api/v1/tickets/${ticketId}/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          agentName: agentName
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔴 AGENT: Failed to accept ticket:', errorText);
        throw new Error(`Failed to accept ticket: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('✅ AGENT: Successfully accepted ticket:', result);
      
      // Remove accepted ticket from list
      setTickets(prev => prev.filter(t => t.ticketId !== ticketId));
      
      // Navigate to video session using URL provided by backend
      if (result.agentJoinUrl) {
        // Extract org ID from current URL if present
        const pathname = window.location.pathname;
        const orgMatch = pathname.match(/\/(org_[a-zA-Z0-9_]+)\//);
        const orgPrefix = orgMatch ? `/${orgMatch[1]}` : '';
        
        let joinUrl;
        if (result.agentJoinUrl.startsWith('http')) {
          // Full URL from backend - need to add org prefix to path
          const url = new URL(result.agentJoinUrl);
          const pathWithOrg = `${orgPrefix}${url.pathname}`;
          joinUrl = `${url.origin}${pathWithOrg}${url.search}`;
        } else if (result.agentJoinUrl.startsWith('/')) {
          // Relative URL - just add org prefix
          joinUrl = `${orgPrefix}${result.agentJoinUrl}`;
        } else {
          // Use as-is
          joinUrl = result.agentJoinUrl;
        }
        
        console.log('🎯 [AgentDashboard] Redirecting to video session:', {
          backendUrl: result.agentJoinUrl,
          finalUrl: joinUrl,
          orgPrefix,
          pathname
        });
        
        window.location.href = joinUrl;
      }
      
      if (onAcceptTicket) {
        onAcceptTicket(ticketId);
      }
      
    } catch (error) {
      console.error('🔴 AGENT: Error accepting ticket:', error);
      setError('Failed to accept ticket. Please try again.');
    } finally {
      setIsAccepting(null);
    }
  };

  // Reject ticket (proper API implementation)
  const rejectTicket = async (ticketId: string) => {
    if (!apiKey) {
      setError('No API key available');
      return;
    }
    
    console.log('❌ AGENT: Rejecting ticket:', ticketId);
    
    try {
      const response = await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev/api/v1/tickets/${ticketId}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          agentName: agentName,
          reason: 'Agent declined'
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔴 AGENT: Failed to reject ticket:', errorText);
        throw new Error(`Failed to reject ticket: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('✅ AGENT: Successfully rejected ticket:', result);
      
      // Remove rejected ticket from list
      setTickets(prev => prev.filter(t => t.ticketId !== ticketId));
      
      // Clear selection if this ticket was selected
      if (selectedTicket?.ticketId === ticketId) {
        setSelectedTicket(null);
      }
      
    } catch (error) {
      console.error('🔴 AGENT: Error rejecting ticket:', error);
      setError('Failed to reject ticket. Please try again.');
    }
  };

  const handleStartVideoCall = (ticketId: string) => {
    if (onStartVideoCall) {
      onStartVideoCall(ticketId);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'primary';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'technical': return '🔧';
      case 'billing': return '💳';
      case 'product': return '📦';
      case 'general': return '💬';
      default: return '❓';
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    if (filter === 'new' && ticket.status !== 'waiting') return false; // Support tickets start with 'waiting' status
    if (filter === 'my_tickets' && ticket.assignedTo !== agentName) return false;
    if (searchQuery && !ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !ticket.customerName.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  // Check for authentication and API key after hooks are called
  if (!isAuthenticated) {
    console.error('SECURITY ERROR: User not authenticated');
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg">
          <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-red-600 mb-2">Authentication Required</h2>
          <p className="text-gray-600 mb-4">You must be signed in to access the agent dashboard.</p>
          <p className="text-sm text-gray-500">Please sign in to continue.</p>
        </div>
      </div>
    );
  }
  
  // Note: API key check removed - authenticated users should always have access
  // The backend will handle organization-specific data filtering based on the API key

  // Show API key input if not set (exactly like legal template)
  if (showApiKeyInput) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--brand-background)' }}>
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md border" style={{ borderColor: 'var(--brand-border)' }}>
          <div className="text-center mb-6">
            <BrandedHeader size="small" showTagline={false} />
            <h2 className="text-2xl font-bold mt-4" style={{ color: 'var(--brand-text-primary)' }}>
              Support Agent Dashboard
            </h2>
            <p className="mt-2" style={{ color: 'var(--brand-text-secondary)' }}>
              Enter your organization's API key to access the support dashboard
            </p>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--brand-text-primary)' }}>
                API Key
              </label>
              <input
                type="password"
                placeholder="pk_live_..."
                value={apiKeyInput}
                onChange={(e) => setApiKeyInput(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border text-sm"
                style={{ 
                  borderColor: 'var(--brand-border)',
                  backgroundColor: 'var(--brand-background)'
                }}
                onKeyPress={(e) => e.key === 'Enter' && handleApiKeySubmit()}
              />
            </div>
            
            {error && (
              <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg border border-red-200">
                {error}
              </div>
            )}
            
            <button
              onClick={handleApiKeySubmit}
              disabled={!apiKeyInput}
              className="w-full py-3 rounded-lg font-medium text-white transition-colors disabled:opacity-50"
              style={{ 
                backgroundColor: 'var(--brand-primary)',
                opacity: apiKeyInput ? 1 : 0.5
              }}
            >
              Connect to Dashboard
            </button>
          </div>
          
          <div className="mt-6 text-center text-sm" style={{ color: 'var(--brand-text-muted)' }}>
            <p>Need an API key? Contact your system administrator.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--brand-background)' }}>
      {/* Header */}
      <header className="border-b" style={{ backgroundColor: 'var(--brand-surface)', borderColor: 'var(--brand-border)' }}>
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <BrandedHeader size="small" showTagline={false} />
              <div className="flex items-center space-x-2 pl-4 border-l" style={{ borderColor: 'var(--brand-border)' }}>
                <Headphones size={20} style={{ color: 'var(--brand-primary)' }} />
                <span className="font-medium" style={{ color: 'var(--brand-text-primary)' }}>
                  Support Dashboard
                </span>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Agent Status */}
              <div className="flex items-center space-x-2">
                <span className="text-sm" style={{ color: 'var(--brand-text-secondary)' }}>
                  {agentName}
                </span>
                <select
                  value={agentStatus}
                  onChange={(e) => setAgentStatus(e.target.value as any)}
                  className="text-sm px-3 py-1 rounded-lg border"
                  style={{ 
                    borderColor: 'var(--brand-border)',
                    backgroundColor: 'var(--brand-surface)'
                  }}
                >
                  <option value="available">🟢 Available</option>
                  <option value="busy">🟡 Busy</option>
                  <option value="offline">🔴 Offline</option>
                </select>
              </div>
              
              {/* Stats */}
              <div className="flex items-center space-x-4 text-sm">
                <div className="flex items-center space-x-1">
                  <CheckCircle size={16} style={{ color: 'var(--brand-success)' }} />
                  <span style={{ color: 'var(--brand-text-secondary)' }}>12 Resolved Today</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Star size={16} style={{ color: 'var(--brand-warning)' }} />
                  <span style={{ color: 'var(--brand-text-secondary)' }}>4.8 Rating</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex h-[calc(100vh-80px)]">
        {/* Left Panel - Ticket Queue */}
        <div className="w-96 border-r overflow-y-auto" style={{ backgroundColor: 'var(--brand-surface)', borderColor: 'var(--brand-border)' }}>
          {/* Filters and Search */}
          <div className="p-4 border-b" style={{ borderColor: 'var(--brand-border)' }}>
            <div className="mb-3">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: 'var(--brand-text-muted)' }} />
                <input
                  type="text"
                  placeholder="Search tickets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg border text-sm"
                  style={{ 
                    borderColor: 'var(--brand-border)',
                    backgroundColor: 'var(--brand-background)'
                  }}
                />
              </div>
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={() => setFilter('all')}
                className={`flex-1 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  filter === 'all' ? 'font-medium' : ''
                }`}
                style={{
                  backgroundColor: filter === 'all' ? 'var(--brand-primary)' : 'transparent',
                  color: filter === 'all' ? 'white' : 'var(--brand-text-secondary)'
                }}
              >
                All ({tickets.length})
              </button>
              <button
                onClick={() => setFilter('new')}
                className={`flex-1 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  filter === 'new' ? 'font-medium' : ''
                }`}
                style={{
                  backgroundColor: filter === 'new' ? 'var(--brand-primary)' : 'transparent',
                  color: filter === 'new' ? 'white' : 'var(--brand-text-secondary)'
                }}
              >
                New ({tickets.filter(t => t.status === 'waiting').length})
              </button>
              <button
                onClick={() => setFilter('my_tickets')}
                className={`flex-1 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  filter === 'my_tickets' ? 'font-medium' : ''
                }`}
                style={{
                  backgroundColor: filter === 'my_tickets' ? 'var(--brand-primary)' : 'transparent',
                  color: filter === 'my_tickets' ? 'white' : 'var(--brand-text-secondary)'
                }}
              >
                My Tickets ({tickets.filter(t => t.assignedTo === agentName).length})
              </button>
            </div>
          </div>

          {/* Ticket List */}
          <div className="divide-y" style={{ borderColor: 'var(--brand-border)' }}>
            {filteredTickets.map(ticket => (
              <div
                key={ticket.ticketId}
                onClick={() => setSelectedTicket(ticket)}
                className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                  selectedTicket?.ticketId === ticket.ticketId ? 'bg-gray-50 dark:bg-gray-800' : ''
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{getCategoryIcon(ticket.category)}</span>
                      <h3 className="font-medium" style={{ color: 'var(--brand-text-primary)' }}>
                        {ticket.customerName}
                      </h3>
                      <BrandedBadge variant={getPriorityColor(ticket.priority)} size="small">
                        {ticket.priority.toUpperCase()}
                      </BrandedBadge>
                    </div>
                    <p className="text-sm mt-1 line-clamp-1" style={{ color: 'var(--brand-text-primary)' }}>
                      {ticket.subject}
                    </p>
                    <p className="text-xs mt-1 line-clamp-2" style={{ color: 'var(--brand-text-secondary)' }}>
                      {ticket.description}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-xs" style={{ color: 'var(--brand-text-muted)' }}>
                  <div className="flex items-center space-x-3">
                    <span className="flex items-center">
                      <Clock size={12} className="mr-1" />
                      {ticket.waitTime}m wait
                    </span>
                    {ticket.previousInteractions && ticket.previousInteractions > 0 && (
                      <span className="flex items-center">
                        <RefreshCw size={12} className="mr-1" />
                        {ticket.previousInteractions} prev
                      </span>
                    )}
                  </div>
                  <span className="text-xs font-mono">#{ticket.ticketId}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Panel - Ticket Details */}
        <div className="flex-1 flex flex-col" style={{ backgroundColor: 'var(--brand-background)' }}>
          {selectedTicket ? (
            <>
              {/* Ticket Header */}
              <div className="border-b p-6" style={{ borderColor: 'var(--brand-border)' }}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center space-x-3 mb-2">
                      <h2 className="text-xl font-semibold" style={{ color: 'var(--brand-text-primary)' }}>
                        {selectedTicket.subject}
                      </h2>
                      <BrandedBadge variant={getPriorityColor(selectedTicket.priority)}>
                        {selectedTicket.priority.toUpperCase()} Priority
                      </BrandedBadge>
                      <BrandedBadge variant="secondary">
                        {selectedTicket.status.replace('_', ' ').toUpperCase()}
                      </BrandedBadge>
                    </div>
                    
                    <div className="flex items-center space-x-4 text-sm" style={{ color: 'var(--brand-text-secondary)' }}>
                      <span className="flex items-center">
                        <User size={14} className="mr-1" />
                        {selectedTicket.customerName}
                      </span>
                      <span className="flex items-center">
                        <Mail size={14} className="mr-1" />
                        {selectedTicket.email}
                      </span>
                      <span className="flex items-center">
                        <Calendar size={14} className="mr-1" />
                        {new Date(selectedTicket.createdAt).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {selectedTicket.status === 'waiting' ? (
                      <BrandedButton
                        onClick={() => acceptTicket(selectedTicket.ticketId)}
                        disabled={isAccepting === selectedTicket.ticketId}
                        className="flex items-center space-x-2"
                      >
                        {isAccepting === selectedTicket.ticketId ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Connecting...</span>
                          </>
                        ) : (
                          <>
                            <span>Accept Ticket</span>
                          </>
                        )}
                      </BrandedButton>
                    ) : null}
                    
                    {selectedTicket.status === 'waiting' && (
                      <BrandedButton
                        onClick={() => rejectTicket(selectedTicket.ticketId)}
                        variant="outline"
                        className="flex items-center space-x-2"
                      >
                        <span>Decline</span>
                      </BrandedButton>
                    )}
                    
                    {selectedTicket.assignedTo === agentName && (
                      <>
                        <BrandedButton
                          onClick={() => handleStartVideoCall(selectedTicket.ticketId)}
                          variant="primary"
                        >
                          <Video size={16} className="mr-2" />
                          Start Video Call
                        </BrandedButton>
                        <BrandedButton
                          variant="outline"
                        >
                          <MessageSquare size={16} className="mr-2" />
                          Chat
                        </BrandedButton>
                      </>
                    )}
                    <button className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
                      <MoreVertical size={20} style={{ color: 'var(--brand-text-secondary)' }} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Ticket Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <BrandedCard className="mb-6">
                  <h3 className="font-semibold mb-3" style={{ color: 'var(--brand-text-primary)' }}>
                    Issue Description
                  </h3>
                  <p style={{ color: 'var(--brand-text-secondary)' }}>
                    {selectedTicket.description}
                  </p>
                  
                  {selectedTicket.attachments && selectedTicket.attachments.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--brand-text-primary)' }}>
                        Attachments
                      </h4>
                      <div className="space-y-2">
                        {selectedTicket.attachments.map((file, index) => (
                          <div key={index} className="flex items-center space-x-2 text-sm">
                            <span>📎</span>
                            <a href="#" className="hover:underline" style={{ color: 'var(--brand-primary)' }}>
                              {file}
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </BrandedCard>

                {/* Customer History */}
                {selectedTicket.previousInteractions && selectedTicket.previousInteractions > 0 && (
                  <BrandedCard className="mb-6">
                    <h3 className="font-semibold mb-3" style={{ color: 'var(--brand-text-primary)' }}>
                      Customer History
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span style={{ color: 'var(--brand-text-secondary)' }}>Previous Tickets</span>
                        <span className="font-medium" style={{ color: 'var(--brand-text-primary)' }}>
                          {selectedTicket.previousInteractions}
                        </span>
                      </div>
                      {selectedTicket.satisfaction && (
                        <div className="flex items-center justify-between text-sm">
                          <span style={{ color: 'var(--brand-text-secondary)' }}>Average Satisfaction</span>
                          <div className="flex items-center space-x-1">
                            <Star size={14} className="fill-current" style={{ color: 'var(--brand-warning)' }} />
                            <span className="font-medium" style={{ color: 'var(--brand-text-primary)' }}>
                              {selectedTicket.satisfaction.toFixed(1)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </BrandedCard>
                )}

                {/* Quick Actions */}
                <BrandedCard>
                  <h3 className="font-semibold mb-3" style={{ color: 'var(--brand-text-primary)' }}>
                    Quick Actions
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <BrandedButton variant="outline" size="small">
                      <Send size={14} className="mr-2" />
                      Send Knowledge Base Article
                    </BrandedButton>
                    <BrandedButton variant="outline" size="small">
                      <UserPlus size={14} className="mr-2" />
                      Escalate to Specialist
                    </BrandedButton>
                    <BrandedButton variant="outline" size="small">
                      <Calendar size={14} className="mr-2" />
                      Schedule Follow-up
                    </BrandedButton>
                    <BrandedButton variant="outline" size="small">
                      <CheckCircle size={14} className="mr-2" />
                      Mark as Resolved
                    </BrandedButton>
                  </div>
                </BrandedCard>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Headphones size={64} className="mx-auto mb-4" style={{ color: 'var(--brand-text-muted)' }} />
                <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--brand-text-primary)' }}>
                  Select a Ticket
                </h2>
                <p style={{ color: 'var(--brand-text-secondary)' }}>
                  Choose a ticket from the queue to view details and start helping customers
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};